import React, { useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Avatar,
  Container,
  useMediaQuery,
  Menu,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArticleIcon from '@mui/icons-material/Article';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { logout, updateAdminCredentials } from '../services/api';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';

const drawerWidth = 260;

const menuItems = [
  { text: 'Home', path: '/', icon: <HomeIcon /> },
  { text: 'Dashboard', path: '/admin/dashboard', icon: <DashboardIcon /> },
  { text: 'All Posts', path: '/admin/posts', icon: <ArticleIcon /> },
  { text: 'Create Post', path: '/admin/posts/create', icon: <AddIcon /> },
  { text: 'Resources', path: '/admin/resources', icon: <DescriptionIcon /> },
  { text: 'Settings', path: '/admin/settings', icon: <SettingsIcon /> },
];

function Layout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth(); // Get user from context
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);

  // Profile Edit State
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [updating, setUpdating] = useState(false);

  const handleProfileClick = () => {
    if (user) {
      setEditUsername(user.username);
      setOpenProfileDialog(true);
    }
  };

  const handleProfileUpdate = async () => {
    if (!currentPassword) {
      setUpdateError('Current password is required');
      return;
    }

    setUpdating(true);
    setUpdateError('');

    try {
      await updateAdminCredentials({
        username: editUsername,
        current_password: currentPassword,
        new_password: newPassword || undefined
      });

      enqueueSnackbar('Profile updated successfully', { variant: 'success' });

      // If username changed, we might need to re-login or update context
      // For now, let's close dialog and clear fields. 
      // Context should update if we trigger a reload or if we manually update it.
      // Ideally calls a function in AuthContext to refresh user data.

      setOpenProfileDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      window.location.reload(); // Simplest way to refresh auth state
    } catch (err) {
      console.error(err);
      setUpdateError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    enqueueSnackbar('Logged out successfully', { variant: 'success' });
    navigate('/login');
    handleUserMenuClose();
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const isActive = (path) => {
    if (path === '/admin/dashboard' && location.pathname === '/admin') {
      return true;
    }
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          WorldWise Scholar
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} edge="end">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider />

      <Box
        sx={{
          px: 2,
          py: 3,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.04)'
          },
          transition: 'background-color 0.2s'
        }}
        onClick={handleProfileClick}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.primary.main,
            mr: 2
          }}
        >
          {user?.username?.substring(0, 2).toUpperCase() || 'AD'}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {user?.username || 'Admin User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click to Edit
          </Typography>
        </Box>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, px: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                backgroundColor: isActive(item.path) ?
                  `${theme.palette.primary.main}15` : 'transparent',
                color: isActive(item.path) ?
                  theme.palette.primary.main : 'inherit',
                '&:hover': {
                  backgroundColor: isActive(item.path) ?
                    `${theme.palette.primary.main}25` : `rgba(0,0,0,0.04)`
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isActive(item.path) ?
                    theme.palette.primary.main : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        <ListItem disablePadding sx={{ mb: 0.5, mt: 2 }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: `${theme.palette.error.main}10`,
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: theme.palette.error.main,
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          WorldWise Scholar Admin Dashboard
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: { xs: 'block', md: 'none' }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            WorldWise Scholar
          </Typography>
          <IconButton color="inherit" onClick={handleUserMenuOpen}>
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { handleNavigation('/admin/settings'); handleUserMenuClose(); }}>
              <SettingsIcon fontSize="small" sx={{ mr: 2 }} />
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={!isMobile || mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 0 },
          height: '100%',
          overflow: 'auto',
          bgcolor: 'background.default'
        }}
      >
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Outlet />
        </Container>
      </Box>
      <Dialog open={openProfileDialog} onClose={() => setOpenProfileDialog(false)}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 300 }}>
            {updateError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {updateError}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Username"
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Current Password (Required)"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="New Password (Optional)"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              helperText="Leave blank to keep current password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProfileDialog(false)}>Cancel</Button>
          <Button
            onClick={handleProfileUpdate}
            variant="contained"
            disabled={updating || !currentPassword || !editUsername}
          >
            {updating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Layout;